import Link from "next/link"
import React from "react"
import {
  CheckOldUserExistsEnum,
  useCheckOldUserExistsMutation,
} from "../../../../api/backend/backend-api"
import { forgotPasswordPagePath } from "../../../../root/routing/urls"
import CustomAlert from "../../../../ui/atoms/Alert"
import CustomButton from "../../../../ui/atoms/Button"
import { BackArrowIcon } from "../../../../ui/icons"
import { getQueryParam } from "../../../../utils/url"
import { EditorialLabel } from "../../../contents"
import { buildPasswordResetUrl } from "../../navigation/urls"
import LoginForm from "../LoginForm"
import PreRegistrationForm, {
  PreRegistrationFormData,
} from "../PreRegistrationForm"
import RegisterForm from "../RegisterForm"

type CheckStatus =
  | "alreadyRegistered"
  | "existsInCrm"
  | "phoneInUse"
  | "crmUserUnderage"
type RegistrationStep = "check" | "register"

const RegistrationFlow = () => {
  const [initialized, setInitialized] = React.useState(false)
  const [step, setStep] = React.useState<RegistrationStep>("check")
  const [email, setEmail] = React.useState("")
  const [phone, setPhone] = React.useState("+39")
  const [prefilledFirstName, setPrefilledFirstName] = React.useState("")
  const [prefilledLastName, setPrefilledLastName] = React.useState("")
  const [returnUrl, setReturnUrl] = React.useState("")
  const [checkStatus, setCheckStatus] = React.useState<CheckStatus>()

  const [check, { loading: checkLoading }] = useCheckOldUserExistsMutation()

  const handleCheck = async (
    data: PreRegistrationFormData,
    returnUrl: string
  ) => {
    setEmail(data.email.trim())
    setPhone(data.phone.trim())

    const result = await check({
      variables: {
        input: {
          email: data.email.trim(),
          phone: data.phone.trim(),
          passwordResetBaseUrl: buildPasswordResetUrl(),
          returnUrl,
        },
      },
    })

    if (
      result.data?.checkOldUserExists.enum === CheckOldUserExistsEnum.NewUser
    ) {
      setStep("register")
      setCheckStatus(undefined)
    }

    if (
      result.data?.checkOldUserExists.enum ===
      CheckOldUserExistsEnum.ExistsCrmOnly
    ) {
      setCheckStatus("existsInCrm")
    }

    if (
      result.data?.checkOldUserExists.enum === CheckOldUserExistsEnum.ExistsBoth
    ) {
      setCheckStatus("alreadyRegistered")
    }

    if (
      result.data?.checkOldUserExists.enum === CheckOldUserExistsEnum.Underage
    ) {
      setCheckStatus("crmUserUnderage")
    }

    if (
      result.data?.checkOldUserExists.enum ===
        CheckOldUserExistsEnum.PhoneInUseByCrmUser ||
      result.data?.checkOldUserExists.enum ===
        CheckOldUserExistsEnum.PhoneInUseByLocalUser
    ) {
      setCheckStatus("phoneInUse")
    }
  }

  React.useEffect(() => {
    const emailFromQuery = getQueryParam(window.location, "email")
    if (emailFromQuery) {
      setEmail(emailFromQuery.trim())
    }

    const phoneFromQuery = getQueryParam(window.location, "phone")
    if (phoneFromQuery) {
      setPhone(phoneFromQuery.trim())
    }

    const firstNameFromQuery = getQueryParam(window.location, "firstName")
    if (firstNameFromQuery) {
      setPrefilledFirstName(firstNameFromQuery.trim())
    }

    const lastNameFromQuery = getQueryParam(window.location, "lastName")
    if (lastNameFromQuery) {
      setPrefilledLastName(lastNameFromQuery.trim())
    }

    const returnUrlFromQuery = getQueryParam(window.location, "returnUrl")
    if (returnUrlFromQuery) {
      setReturnUrl(returnUrlFromQuery)
    }

    if (emailFromQuery && phoneFromQuery) {
      handleCheck(
        {
          email: emailFromQuery,
          phone: phoneFromQuery,
        },
        returnUrlFromQuery
      )
    }

    setInitialized(true)
  }, [])

  const onBack = () => {
    setStep("check")
    setCheckStatus(undefined)
  }

  if (!initialized) {
    return <></>
  }

  return (
    <>
      {step === "check" && checkStatus !== "alreadyRegistered" && (
        <>
          <PreRegistrationForm
            // email={email}
            // onEmailChange={setEmail}
            // phone={phone}
            // onPhoneChange={setPhone}
            defaultEmail={email}
            defaultPhone={phone}
            onSubmit={(data) => handleCheck(data, returnUrl)}
            loading={checkLoading}
            className="mt-6 mb-8"
          />
          {checkStatus === "phoneInUse" && (
            <div>
              <CustomAlert severity="warning">
                <EditorialLabel scope="account" id="phone-in-use" plainText />
              </CustomAlert>
            </div>
          )}
          {checkStatus === "crmUserUnderage" && (
            <div>
              <CustomAlert severity="warning">
                <EditorialLabel
                  scope="account"
                  id="crm-user-underage"
                  plainText
                />
              </CustomAlert>
            </div>
          )}
        </>
      )}
      {step === "register" && (
        <RegisterForm
          phone={phone}
          defaultEmail={email.trim()}
          defaultFirstName={prefilledFirstName?.trim()}
          defaultLastName={prefilledLastName?.trim()}
          redirectUrl={returnUrl}
          onBack={onBack}
        />
      )}
      {checkStatus === "alreadyRegistered" && (
        <div>
          <CustomButton variant="text" onClick={onBack} vCenter noGutters>
            <BackArrowIcon className="mr-2" />
            <EditorialLabel scope="common" id="back" plainText />
          </CustomButton>
          <CustomAlert>
            <EditorialLabel scope="account" id="already-registered" plainText />
          </CustomAlert>
          <LoginForm defaultEmail={email} redirectUrl={returnUrl} />
          <div className="my-8">
            <Link href={forgotPasswordPagePath} passHref>
              <a>
                <EditorialLabel
                  scope="account"
                  id="forgot-password"
                  variant="body1"
                />
              </a>
            </Link>
          </div>
        </div>
      )}
      {checkStatus === "existsInCrm" && (
        <div>
          <CustomAlert>
            <EditorialLabel scope="account" id="old-user" plainText />
          </CustomAlert>
        </div>
      )}
    </>
  )
}

export default RegistrationFlow
